import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import ThemeToggle from './ThemeToggle';
import Button from './Button';
import LogoBranca from '../assets/logoAzul.png';
import LogoAzul from '../assets/logoAzul.png';
import useTheme from '../context/ThemeContext';

const solutions = [
  {
    to: '/product/fazenda-leiteira',
    icon: 'fa-solid fa-cow',
    thumb: '🐄',
    label: 'Fazenda Leiteira',
    desc: 'Gestão rural, produção, custos e lucro.',
    gradient: 'linear-gradient(135deg, #22c55e, #38bdf8)',
  },
  {
    to: '/product/controle-de-obras',
    icon: 'fa-solid fa-helmet-safety',
    thumb: '🏗️',
    label: 'Controle de Obras',
    desc: 'Etapas, custos, cronograma e relatórios.',
    gradient: 'linear-gradient(135deg, #f59e0b, #3b82f6)',
  },
  {
    to: '/product/locafacil-obras',
    icon: 'fa-solid fa-truck-ramp-box',
    thumb: '🚜',
    label: 'LocaFácil Obras',
    desc: 'Locação, contratos e equipamentos.',
    gradient: 'linear-gradient(135deg, #06b6d4, #6366f1)',
  },
  {
    to: '/product/atende24h',
    icon: 'fa-solid fa-robot',
    thumb: '🤖',
    label: 'Atende24h IA',
    desc: 'Atendimento inteligente por WhatsApp.',
    gradient: 'linear-gradient(135deg, #2563eb, #06b6d4)',
  },
];

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/planos', label: 'Planos' },
  { to: '/sobre', label: 'Sobre' },
  { to: '/contato', label: 'Contato' },
];

const MobileMenu = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;

    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = original || 'auto';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="mobile-menu"
        initial={{ opacity: 0, x: '100%' }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: '100%' }}
        transition={{ type: 'spring', stiffness: 120, damping: 18 }}
        className="premium-mobile-menu"
      >
        <div className="mobile-menu-header">
          <span>Menu</span>

          <motion.i
            className="fas fa-times"
            whileHover={{ rotate: 90 }}
            onClick={onClose}
          />
        </div>

        <div className="mobile-menu-content">
          <Link to="/" className="mobile-main-link" onClick={onClose}>
            Home
          </Link>

          <div className="mobile-section-title">Soluções</div>

          {solutions.map((item, index) => (
            <motion.div
              key={item.to}
              initial={{ opacity: 0, x: 18 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link to={item.to} className="mobile-solution-card" onClick={onClose}>
                <div
                  className="mobile-solution-thumb"
                  style={{ background: item.gradient }}
                >
                  <span>{item.thumb}</span>
                </div>

                <div>
                  <strong>{item.label}</strong>
                  <small>{item.desc}</small>
                </div>
              </Link>
            </motion.div>
          ))}

          <div className="mobile-section-title">Institucional</div>

          {navLinks
            .filter((link) => link.to !== '/')
            .map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="mobile-main-link"
                onClick={onClose}
              >
                {link.label}
              </Link>
            ))}

          <Button
            to="/contato"
            style={{
              width: '100%',
              justifyContent: 'center',
              marginTop: '1.5rem',
            }}
            onClick={onClose}
          >
            Solicitar Demonstração
          </Button>
        </div>

        <style>{mobileStyles}</style>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const location = useLocation();
  const { isDark } = useTheme();

  const isSolutionActive =
    location.pathname.includes('fazenda-leiteira') ||
    location.pathname.includes('controle-de-obras') ||
    location.pathname.includes('locafacil-obras') ||
    location.pathname.includes('atende24h');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setSolutionsOpen(false);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  return (
    <>
      <motion.nav
        initial={{ y: -90 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 110, damping: 20 }}
        className={`premium-navbar ${scrolled ? 'is-scrolled' : ''}`}
      >
        <div className="container">
          <div className="premium-navbar-inner">
            <Link to="/" className="premium-logo">
              <img src={isDark ? LogoBranca : LogoAzul} alt="Atende24h" />
            </Link>

            <ul className="premium-desktop-menu">
              <li>
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `premium-nav-link ${isActive ? 'active' : ''}`
                  }
                >
                  Home
                </NavLink>
              </li>

              <li
                className="solutions-dropdown-wrapper"
                onMouseEnter={() => setSolutionsOpen(true)}
                onMouseLeave={() => setSolutionsOpen(false)}
              >
                <button
                  type="button"
                  className={`premium-nav-link dropdown-button ${isSolutionActive ? 'active' : ''
                    }`}
                >
                  Soluções
                  <i className="fa-solid fa-chevron-down"></i>
                </button>

                <AnimatePresence>
                  {solutionsOpen && (
                    <motion.div
                      className="solutions-dropdown"
                      initial={{ opacity: 0, y: 8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.98 }}
                      transition={{ duration: 0.16 }}
                    >
                      <div className="solutions-dropdown-header">
                        <strong>Sistemas para gestão e atendimento</strong>
                        <span>Escolha a solução ideal para seu negócio.</span>
                      </div>

                      <div className="solutions-dropdown-grid">
                        {solutions.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            className="solution-dropdown-card"
                          >
                            <div
                              className="solution-dropdown-thumb"
                              style={{ background: item.gradient }}
                            >
                              <span>{item.thumb}</span>
                            </div>

                            <div className="solution-dropdown-content">
                              <strong>{item.label}</strong>
                              <span>{item.desc}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                      <div className="solutions-dropdown-footer">
                        <Link to="/solucoes" className="solutions-see-all">
                          Ver todas as soluções
                          <i className="fa-solid fa-arrow-right"></i>
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>

              {navLinks
                .filter((link) => link.to !== '/')
                .map((link) => (
                  <li key={link.to}>
                    <NavLink
                      to={link.to}
                      className={({ isActive }) =>
                        `premium-nav-link ${isActive ? 'active' : ''}`
                      }
                    >
                      {link.label}
                    </NavLink>
                  </li>
                ))}

              <li>
                <ThemeToggle />
              </li>

              <li>
                <Button to="/contato">Solicitar Demonstração</Button>
              </li>
            </ul>

            <button
              type="button"
              className="premium-mobile-button"
              onClick={() => setIsOpen(true)}
              aria-label="Abrir menu"
            >
              <i className="fas fa-bars"></i>
            </button>
          </div>
        </div>

        <style>{desktopStyles}</style>
      </motion.nav>

      <MobileMenu isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

const desktopStyles = `
  .premium-navbar {
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 5000;
    background: var(--navbar-bg);
    backdrop-filter: blur(18px);
    border-bottom: 1px solid var(--border-color);
    transition: all .25s ease;
  }

  .premium-navbar.is-scrolled {
    box-shadow: 0 14px 40px rgba(0, 0, 0, .12);
  }

  .premium-navbar-inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: .85rem 0;
  }

  .premium-logo {
    display: flex;
    align-items: center;
    text-decoration: none;
  }

  .premium-logo img {
    width: 150px;
    display: block;
  }

  .premium-desktop-menu {
    display: flex;
    align-items: center;
    gap: 1.4rem;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .premium-desktop-menu > li {
    position: relative;
    flex: 0 0 auto;
  }

  .premium-nav-link {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: .35rem;
    border: 0;
    background: transparent;
    color: var(--text-color);
    text-decoration: none;
    font-size: .94rem;
    font-weight: 600;
    letter-spacing: .2px;
    cursor: pointer;
    padding: .55rem 0;
    transition: color .22s ease;
    white-space: nowrap;
  }

  .premium-nav-link::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: .25rem;
    width: 100%;
    height: 2px;
    border-radius: 999px;
    background: linear-gradient(90deg, var(--primary), var(--primary-soft));
    transform: scaleX(0);
    transform-origin: left;
    transition: transform .22s ease;
  }

  .premium-nav-link:hover,
  .premium-nav-link.active {
    color: var(--primary);
  }

  .premium-nav-link:hover::after,
  .premium-nav-link.active::after {
    transform: scaleX(1);
  }

  .premium-nav-link i {
    font-size: .72rem;
    margin-top: 1px;
  }

  .solutions-dropdown-wrapper {
    position: relative;
  }

  .solutions-dropdown {
    position: absolute;
    top: calc(100% + 10px);
    left: 50%;
    width: 660px;
    transform: translateX(-50%);
    padding: 1.15rem;
    border-radius: 26px;
    background:
      linear-gradient(135deg, rgba(255,255,255,.90), rgba(255,255,255,.72));
    border: 1px solid rgba(148, 163, 184, .28);
    box-shadow: 0 28px 90px rgba(15, 23, 42, .20);
    backdrop-filter: blur(24px);
    z-index: 9999;
  }

  html.dark .solutions-dropdown,
  .dark .solutions-dropdown {
    background:
      linear-gradient(135deg, rgba(15,23,42,.94), rgba(8,13,28,.92));
    border-color: rgba(148, 163, 184, .18);
    box-shadow: 0 28px 90px rgba(0, 0, 0, .42);
  }

  .solutions-dropdown::before {
    content: '';
    position: absolute;
    top: -18px;
    left: 0;
    right: 0;
    height: 18px;
  }

  .solutions-dropdown-header {
    padding: .35rem .35rem 1rem;
    border-bottom: 1px solid var(--border-color);
    margin-bottom: 1rem;
  }

  .solutions-dropdown-header strong {
    display: block;
    color: var(--text-color);
    font-size: 1rem;
    margin-bottom: .25rem;
  }

  .solutions-dropdown-header span {
    color: var(--text-muted, #64748b);
    font-size: .86rem;
  }

  .solutions-dropdown-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: .8rem;
  }

  .solution-dropdown-card {
    display: flex;
    gap: .85rem;
    align-items: center;
    padding: .85rem;
    border-radius: 20px;
    text-decoration: none;
    background: rgba(148, 163, 184, .08);
    border: 1px solid rgba(148, 163, 184, .10);
    transition: all .22s ease;
    min-height: 86px;
  }

  .solution-dropdown-card:hover {
    transform: translateY(-2px);
    border-color: rgba(56, 189, 248, .36);
    background: rgba(56, 189, 248, .10);
  }

  .solution-dropdown-thumb {
    width: 64px;
    height: 64px;
    border-radius: 18px;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    box-shadow: inset 0 1px 0 rgba(255,255,255,.35);
    overflow: hidden;
  }

  .solution-dropdown-thumb span {
    font-size: 1.85rem;
    filter: drop-shadow(0 4px 10px rgba(0,0,0,.18));
  }

  .solution-dropdown-content strong {
    display: block;
    color: var(--text-color);
    font-size: .94rem;
    margin-bottom: .24rem;
  }

  .solution-dropdown-content span {
    display: block;
    color: var(--text-muted, #64748b);
    font-size: .8rem;
    line-height: 1.35;
  }

  .premium-mobile-button {
    display: none;
    border: 0;
    background: transparent;
    color: var(--text-color);
    font-size: 1.45rem;
    cursor: pointer;
  }

  @media (max-width: 1100px) {
    .premium-desktop-menu {
      gap: 1rem;
    }

    .premium-logo img {
      width: 135px;
    }
  }

  @media (max-width: 980px) {
    .premium-desktop-menu {
      display: none;
    }

    .premium-mobile-button {
      display: block;
    }
  }
`;

const mobileStyles = `
  .premium-mobile-menu {
    position: fixed;
    inset: 0;
    width: 100vw;
    max-width: 100vw;
    box-sizing: border-box;
    overflow-x: hidden;
    background:
      radial-gradient(circle at top right, rgba(56,189,248,.18), transparent 32%),
      radial-gradient(circle at bottom left, rgba(99,102,241,.18), transparent 35%),
      #050816;
    z-index: 999999;
    display: flex;
    flex-direction: column;
    padding: 1.4rem;
    color: #fff;
  }

  .mobile-menu-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.4rem;
  }

  .mobile-menu-header span {
    font-weight: 800;
    font-size: 1.15rem;
  }

  .mobile-menu-header i {
    cursor: pointer;
    font-size: 1.35rem;
  }

  .mobile-menu-content {
    flex: 1;
    overflow-y: auto;
    padding-right: .2rem;
  }

  .mobile-section-title {
    margin: 1.4rem 0 .75rem;
    color: #94a3b8;
    font-size: .74rem;
    font-weight: 800;
    letter-spacing: .12em;
    text-transform: uppercase;
  }

  .mobile-main-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: .95rem 0;
    color: #fff;
    text-decoration: none;
    font-size: 1.05rem;
    font-weight: 650;
    border-bottom: 1px solid rgba(255,255,255,.08);
  }

  .mobile-solution-card {
    display: flex;
    gap: .85rem;
    align-items: center;
    padding: .9rem;
    margin-bottom: .7rem;
    border-radius: 18px;
    text-decoration: none;
    background: rgba(255,255,255,.06);
    border: 1px solid rgba(255,255,255,.09);
  }

  .mobile-solution-thumb {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
  }

  .mobile-solution-thumb span {
    font-size: 1.55rem;
  }

  .mobile-solution-card strong {
    display: block;
    color: #fff;
    font-size: .96rem;
    margin-bottom: .25rem;
  }

  .mobile-solution-card small {
    display: block;
    color: #cbd5e1;
    font-size: .8rem;
    line-height: 1.35;
  }
`;

export default Navbar;